import ensureUserData from 'utils/ensureUserData';
import {
  verifyRequiredBetaFeatures,
  verifyRequiredBetaFeaturesWithUserLoginStatus,
} from 'utils/betaFeatures/verifyRequiredBetaFeatures';
import {
  redirectToSSO,
  redirectToSSOChangeCredentials,
  redirectToSSOLoginElevated,
} from 'apps/sso/redirectHandlers';
import shopControlsVisibilities from 'apps/header/utils/shopControlsVisibilities';

import RootView from 'apps/root/containers/RootView';
import NotFoundPage from 'apps/notFound/components/NotFoundPage';
import NotAvailablePage from 'apps/notAvailable/components/NotAvailablePage';

import bopRedirects from 'apps/products/redirects';
import catalogueRedirects from 'apps/catalogue/redirects';
import couponsRedirects from 'apps/coupons/redirects';
import { homepageLoggedOut } from 'apps/loggedOutHomePage/routes';
import { redirectsFromLegacyToRavenHomepage } from 'apps/loggedInHomepage/redirects';
import { loggedInHomepageRoute } from 'apps/loggedInHomepage/routes';
import deliveryPassRedirects from '[theme-lib]/apps/deliveryPass/redirects';
import navigationRedirects from 'apps/navigation/redirects';
import staticContentRedirects from 'apps/staticContent/redirects';
import staticRedirects from '[theme-lib]/staticHtml/redirects';
import ordersReservedRedirects from 'apps/ordersReserved/redirects';
import settingsRedirects from 'apps/settings/redirects';
import { pageViewIdGenerator } from 'apps/analytics';

import paymentsRawRoutes from 'apps/payments/rawRoutes';
import bopRoutes from 'apps/products/routes';
import catalogueRoutes from 'apps/catalogue/routes';
import computeLinks from 'apps/root/utils/computeLinks';
import couponsRoutes from 'apps/coupons/routes';
import deliveryRoutes from 'apps/delivery/routes';
import { redirectToBroker } from 'apps/delivery/onEnterHandlers';
import deliveryPassRoutes from '[theme-lib]/apps/deliveryPass/routes';
import deliveryPassMemberRoutes from '[theme-lib]/apps/deliveryPassManagement/routes';
import navigationRoutes from 'apps/navigation/routes';
import staticContentRoutes from 'apps/staticContent/routes';
import settingsRoutes from 'apps/settings/routes';
import paymentsRoutes from 'apps/payments/routes';
import ordersReservedRoutes from 'apps/ordersReserved/routes';
import subscriptionsRoutes from 'apps/subscriptions/routes';
import checkoutRoutes from 'apps/checkout/routes';
import referAFriendRoutes from 'apps/referAFriend/routes';

// routes below are available only on CIT environment
import styleguideRoutes from 'apps/styleguide/routes';
import sentryRoutes from 'apps/sentryTestSuite/routes';
import visualRegressionRoutes from 'apps/visualregression/routes';

import { AuthenticationLevel } from './auth';
import { requiredLoginLevel } from './utils/routes/requiredLoginLevel';
import { redirectOnLoginLevel } from './utils/routes/redirectOnLoginLevel';
import { BetaFeatures } from 'apps/login/consts';
import { USER_LOGIN_STATUS } from 'apps/user/helpers';

const redirectLoggedInUserToHomepage = verifyRequiredBetaFeaturesWithUserLoginStatus(
  [{ auth: USER_LOGIN_STATUS.LOGGED_OUT, betaFeatures: [] }],
  { redirectOnReject: '/', setNoRouteAccessOnReject: false },
);

const redirectToQuickRegComplete = (nextState, replace) => replace('/webshop/quickRegComplete.do?dnr=true');

// SSO Redirect for predefined postcode
const redirectToHomepage = (nextState, replace) => replace('/');

export default [
  paymentsRawRoutes,
  ...staticRedirects,
  {
    path: '/',
    onEnterHandlers: [
      ensureUserData,
      computeLinks,
      pageViewIdGenerator,
    ],
    component: RootView,
    indexRoute: homepageLoggedOut,
    shopControlsVisibility: shopControlsVisibilities.SLOT_BOOKING_AND_CHECKOUT,
    basketVisible: true,
    childRoutes: [
      // global apps routes
      {
        path: 'login',
        onEnterHandlers: [
          redirectToBroker,
          redirectLoggedInUserToHomepage,
          redirectToSSO('login'),
        ],
        basketVisible: false,
      },
      {
        path: 'change-credentials',
        onEnterHandlers: [
          redirectToSSOChangeCredentials(),
        ],
      },
      {
        path: 'register',
        onEnterHandlers: [
          redirectLoggedInUserToHomepage,
          redirectToSSO('registration'),
        ],
        basketVisible: false,
        queryParamsDisablingModalView: [
          'directPostcode',
        ],
        getComponent(location, callback) {
          import(/* webpackChunkName: "login" */ 'apps/sso/containers/RegistrationSSOWithPredefinedPostcode')
            .then(component => callback(null, component.default));
        },
      },
      {
        path: 'register/completed',
        onEnterHandlers: [redirectToQuickRegComplete],
      },
      {
        path: 'login-elevated',
        onEnterHandlers: [
          verifyRequiredBetaFeatures([BetaFeatures.LOGIN_ELEVATED_RAVEN]),
          requiredLoginLevel(AuthenticationLevel.REMEMBERED),
          redirectOnLoginLevel(AuthenticationLevel.ELEVATED),
          redirectToSSOLoginElevated(),
        ],
        getComponent(location, callback) {
          import(/* webpackChunkName: "login" */ 'apps/login/containers/LoginElevated')
            .then(component => callback(null, component.default));
        },
      },
      {
        path: 'not-found',
        component: NotFoundPage,
      },
      {
        path: 'not-available',
        component: NotAvailablePage,
      },
      loggedInHomepageRoute,
      deliveryRoutes,
      // redirects routes
      {
        path: 'webshop',
        indexRoute: { onEnterHandlers: [redirectToHomepage] },
        childRoutes: [
          ...bopRedirects,
          catalogueRedirects,
          ...couponsRedirects,
          ...deliveryPassRedirects,
          ...navigationRedirects,
          ...staticContentRedirects,
          ...ordersReservedRedirects,
          ...settingsRedirects,
          ...redirectsFromLegacyToRavenHomepage,
        ],
      },
      // lazy load apps routes
      ...bopRoutes,
      ...catalogueRoutes,
      ...couponsRoutes,
      ...deliveryPassRoutes,
      ...deliveryPassMemberRoutes,
      ...navigationRoutes,
      ...staticContentRoutes,
      ...settingsRoutes,
      ...paymentsRoutes,
      ...ordersReservedRoutes,
      ...checkoutRoutes,
      ...referAFriendRoutes,

      // routes below are available only on CIT environment
      ...styleguideRoutes,
      ...sentryRoutes,
      ...visualRegressionRoutes,

      {
        childRoutes: [
          ...subscriptionsRoutes,
        ],
      },
    ],
  },
];
